import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { orderBy, filterBy } from '@progress/kendo-data-query'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { Switch } from '@progress/kendo-react-inputs'
import { MultiSelect, MultiSelectChangeEvent } from '@progress/kendo-react-dropdowns'
import { pipelineData, targetQuoteTypes } from '../../data/interfaces'
import { getPipelineData } from '../../data/pipelineData'
import { getQuotesByLikelihood } from '../../data/quoteData'
import { getCurrentUserEmployee } from '../../data/employeeData'
import ReportUserFilter, { useReportUserFilter } from '../../components/ReportUserFilter/ReportUserFilter'
import { getWorktypeDropdown } from '../../data/worktypeData'
import { dropDown } from '../../data/interfaces'
import { useToast } from '../../contexts/ToastContext'
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout'
import { Button } from '@progress/kendo-react-buttons'
// import '@progress/kendo-theme-fluent/dist/all.css' // Now imported globally
// import '../Styles.css' // Temporarily disabled to test button issues
import './Pipeline.css' // Blue header styling
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ReferenceLine,
} from 'recharts'
import { 
  FaFileExcel,
  FaSearch
} from 'react-icons/fa'

// Full precision for tooltips and tiles; the axes get the compact form so the
// ticks stay readable at chart width.
// Profit can be negative, so the sign leads the symbol: -£86,000, not £-86,000.
const formatCurrency = (value: number) => {
  const amount = Number(value) || 0
  const sign = amount < 0 ? '-' : ''
  return `${sign}£${Math.abs(amount).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}

const formatCompactCurrency = (value: number) => {
  const amount = Number(value) || 0
  const sign = amount < 0 ? '-' : ''
  const abs = Math.abs(amount)
  if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(1)}m`
  if (abs >= 1_000) return `${sign}£${Math.round(abs / 1_000)}k`
  return `${sign}£${Math.round(abs)}`
}

// Recessive ink for axes - deliberately not the navy series colour, so text
// never reads as an identity.
const axisTick = { fontSize: '9pt', fill: '#5a6472' }

// The portal's brand trio. Validated for colour-vision deficiency: navy/green
// separate at deltaE 13.6 under deuteranopia. Darker, "safer looking" greens
// collapse that to ~3, so these stay as they are.
const COLOUR_NAVY = '#1e2a4e'
const COLOUR_GREEN = '#aec741'

// Profit is signed, so it uses the same good/bad pair the grid's Profit column
// already established.
const COLOUR_PROFIT = '#217346'
const COLOUR_LOSS = '#d32f2f'

// Recharts paints legend labels in the series colour by default. Identity is
// the swatch's job; the text stays in muted ink.
const legendText = (value: string) => (
  <span style={{ color: '#5a6472', fontSize: '9pt' }}>{value}</span>
)

// Department names run long ("Mechanical & Ventilation") and collide with
// their neighbours on an angled axis. The tooltip still shows the full name.
const truncateCategory = (value: string) =>
  typeof value === 'string' && value.length > 16 ? `${value.slice(0, 15)}\u2026` : value

interface DepartmentSummary {
  department: string
  totalValue: number
  invoicedTotal: number
  remainingDraw: number
  profit: number
}

/**
 * The pipeline report, and its Completed Jobs copy: the same report for jobs
 * marked as complete only, behind its own role (Reports.CompletedJobs).
 */
export type PipelineVariant = 'pipeline' | 'completedJobs'

const VARIANTS = {
  pipeline: {
    title: 'Pipeline Report',
    permission: 'Pipeline',
    // The pipeline has always needed its own role, administrator or not.
    administratorBypass: false,
    completedOnly: false,
    noun: 'pipeline data',
    jobsLabel: 'Live Jobs',
    valueLabel: 'Total Pipeline Value',
    // The boxes across the top. Completed jobs have nothing left to draw and
    // their profit is no longer a forecast, so that report shows fewer.
    showValueKpi: true,
    invoicedLabel: 'Invoiced to Date',
    showRemainingKpi: true,
    profitLabel: 'Forecast Profit',
    // The Invoiced vs Remaining and Top 10 Remaining charts.
    showDrawCharts: true,
    tabTitle: 'Pipeline',
    exportLabel: 'Export Pipeline',
    sheetName: 'Pipeline Data',
    filePrefix: 'pipeline_report',
  },
  completedJobs: {
    title: 'Completed Jobs Report',
    permission: 'CompletedJobs',
    // Matches the Reports.CompletedJobs policy on the API.
    administratorBypass: true,
    completedOnly: true,
    noun: 'completed jobs',
    jobsLabel: 'Completed Jobs',
    valueLabel: 'Total Value',
    showValueKpi: false,
    invoicedLabel: 'Invoiced',
    showRemainingKpi: false,
    profitLabel: 'Profit',
    showDrawCharts: false,
    tabTitle: 'Completed Jobs',
    exportLabel: 'Export Completed Jobs',
    sheetName: 'Completed Jobs',
    filePrefix: 'completed_jobs_report',
  },
}

interface PipelineProps {
  variant?: PipelineVariant
}

function Pipeline({ variant = 'pipeline' }: PipelineProps) {
  const report = VARIANTS[variant]
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [data, setData] = useState<pipelineData[]>([])
  const [quotesData, setQuotesData] = useState<targetQuoteTypes[]>([])
  const [loading, setLoading] = useState(true)
  const [quotesLoading, setQuotesLoading] = useState(false)
  const [hasPermission, setHasPermission] = useState(false)
  const [activeTab, setActiveTab] = useState(0)

  // Pipeline filters (default = current behaviour: no date range, completed excluded)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [showCompleted, setShowCompleted] = useState(false)
  // Held as the selected dropdown entries rather than bare ids, so the
  // MultiSelect can render its chips; the ids are pulled off when fetching.
  const [selectedWorktypes, setSelectedWorktypes] = useState<dropDown[]>([])
  const [worktypeOptions, setWorktypeOptions] = useState<dropDown[]>([])
  // The report's user filter: jobs by project manager, target quotes by who
  // they are assigned to.
  const users = useReportUserFilter(hasPermission)
  const [pipelineSort, setPipelineSort] = useState<any[]>([])
  const [quotesSort, setQuotesSort] = useState<any[]>([])
  const [pipelineFilter, setPipelineFilter] = useState<any>(null)
  const [quotesFilter, setQuotesFilter] = useState<any>(null)
  const [pipelineSkip, setPipelineSkip] = useState(0)
  const [quotesSkip, setQuotesSkip] = useState(0)
  const [pipelinePageSize] = useState(25)
  const [quotesPageSize] = useState(25)

  // Every filter change refetches, and the pipeline query walks each job in
  // turn, so picking two departments in quick succession leaves two slow
  // requests in flight. Only the newest one is allowed to land.
  const latestPipelineRequest = useRef(0)

  // Fetch pipeline data
  const fetchData = useCallback(async () => {
    const requestId = ++latestPipelineRequest.current
    try {
      setLoading(true)
      const pipelineData = await getPipelineData({
        startDate,
        endDate,
        showCompleted,
        completedOnly: report.completedOnly,
        worktypeIds: selectedWorktypes.map((w) => w.value),
        projectManagerIds: users.effectiveIds,
      })
      if (requestId !== latestPipelineRequest.current) return
      setData(pipelineData)
    } catch (error) {
      if (requestId !== latestPipelineRequest.current) return
      console.error('Error fetching pipeline data:', error)
      showToast(`Error loading ${report.noun}`, 'error')
    } finally {
      // A superseded request must leave the spinner to the one still running.
      if (requestId === latestPipelineRequest.current) {
        setLoading(false)
      }
    }
  }, [showToast, startDate, endDate, showCompleted, selectedWorktypes, users.effectiveIds, report.completedOnly, report.noun])

  // Fetch quotes data with likelihood ID 2
  const fetchQuotesData = useCallback(async () => {
    try {
      setQuotesLoading(true)
      // Spelled out rather than left empty: the quotes list applies no default
      // of its own, so empty would mean everybody's.
      const quotes = await getQuotesByLikelihood(2, users.effectiveIds)
      setQuotesData(quotes)
    } catch (error) {
      console.error('Error fetching quotes data:', error)
      showToast('Error loading quotes data', 'error')
    } finally {
      setQuotesLoading(false)
    }
  }, [showToast, users.effectiveIds])

  // Filter options come from the lookup endpoints rather than the loaded rows -
  // deriving them from the data would make the choices vanish as soon as the
  // user picked one.
  useEffect(() => {
    if (!hasPermission) return

    const loadFilterOptions = async () => {
      try {
        setWorktypeOptions(await getWorktypeDropdown())
      } catch (error) {
        console.error('Error loading pipeline filter options:', error)
        showToast('Failed to load filter options', 'error')
      }
    }

    loadFilterOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission])

  // Check permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const currentUser = await getCurrentUserEmployee()
        if (currentUser && currentUser.permissions) {
          const permitted = currentUser.permissions.some(
            (p) =>
              p.assigned &&
              ((p.area === 'Reports' && p.permission === report.permission) ||
                (report.administratorBypass && p.area === 'Portal' && p.permission === 'Administrator'))
          )
          setHasPermission(permitted)
          if (!permitted) {
            showToast('You do not have permission to access this page', 'error')
            navigate('/')
          }
        } else {
          showToast('Unable to verify permissions', 'error')
          navigate('/')
        }
      } catch (error) {
        console.error('Error checking permissions:', error)
        showToast('Error verifying permissions', 'error')
        navigate('/')
      }
    }
    checkPermissions()
  }, [navigate, showToast, report.permission, report.administratorBypass])

  // Held back until the user filter has its default, so the first load is the
  // one the filter shows.
  useEffect(() => {
    if (!hasPermission || !users.ready) return
    fetchData()
    fetchQuotesData()
  }, [fetchData, fetchQuotesData, hasPermission, users.ready])

  // Prepare data for Kendo Grids with filtering and sorting
  const pipelineDataFiltered = pipelineFilter ? filterBy(data, pipelineFilter) : data
  const pipelineSortedBase = orderBy(pipelineDataFiltered, pipelineSort)
  // 'In defect' jobs always sit at the bottom, keeping the user's sort within each group.
  // A job that is in defect only appears once (at the bottom) — any non-defect duplicate
  // of the same job reference is dropped from its normal position.
  const defectRefs = new Set(
    pipelineSortedBase.filter((row) => row.inDefect).map((row) => row.reference)
  )
  const seenDefectRefs = new Set<string>()
  const pipelineDataSorted = [
    ...pipelineSortedBase.filter((row) => !row.inDefect && !defectRefs.has(row.reference)),
    ...pipelineSortedBase.filter((row) => {
      if (!row.inDefect) return false
      if (seenDefectRefs.has(row.reference)) return false
      seenDefectRefs.add(row.reference)
      return true
    }),
  ]

  // Overview figures. Derived from the sorted/filtered rows rather than the raw
  // response, so the tiles and charts always describe what the grid is showing.
  const sum = (selector: (row: pipelineData) => number) =>
    pipelineDataSorted.reduce((total, row) => total + (Number(selector(row)) || 0), 0)

  const totalValue = sum((r) => r.totalValue)
  const totalInvoiced = sum((r) => r.invoicedTotal)
  const totalCosts = sum((r) => r.totalCosts)
  const totalProfit = sum((r) => r.profit)
  const totalRemaining = sum((r) => r.remainingDraw)
  const marginPct = totalValue > 0 ? (100 * totalProfit) / totalValue : 0
  const invoicedPct = totalValue > 0 ? (100 * totalInvoiced) / totalValue : 0

  const departmentSummary: DepartmentSummary[] = Object.values(
    pipelineDataSorted.reduce((acc: Record<string, DepartmentSummary>, row) => {
      const key = row.department?.trim() || 'Unassigned'
      if (!acc[key]) {
        acc[key] = { department: key, totalValue: 0, invoicedTotal: 0, remainingDraw: 0, profit: 0 }
      }
      acc[key].totalValue += Number(row.totalValue) || 0
      acc[key].invoicedTotal += Number(row.invoicedTotal) || 0
      acc[key].remainingDraw += Number(row.remainingDraw) || 0
      acc[key].profit += Number(row.profit) || 0
      return acc
    }, {})
  ).sort((a, b) => b.totalValue - a.totalValue)

  const profitByDepartment = [...departmentSummary].sort((a, b) => b.profit - a.profit)

  // What is left to bill, biggest first - the most actionable question the
  // pipeline answers. Ten keeps the bars thick enough to read.
  const topRemainingDraw = pipelineDataSorted
    .filter((row) => (Number(row.remainingDraw) || 0) > 0)
    .slice()
    .sort((a, b) => Number(b.remainingDraw) - Number(a.remainingDraw))
    .slice(0, 10)

  // Pale orange background for in-defect rows
  const pipelineRowRender = (tr: React.ReactElement, rowProps: any) => {
    if (!rowProps.dataItem.inDefect) return tr
    return React.cloneElement(
      tr,
      {
        ...tr.props,
        style: { ...tr.props.style, backgroundColor: '#ffe5cc' },
      },
      tr.props.children
    )
  }
  
  const quotesDataFiltered = quotesFilter ? filterBy(quotesData, quotesFilter) : quotesData
  const quotesDataSorted = orderBy(quotesDataFiltered, quotesSort)

  // Export to Excel function
  const exportToExcel = () => {
    const exportData = pipelineDataSorted.map(row => ({
      'Job No': row.reference,
      'Client': row.clientName,
      'Department': row.department || '',
      'Description': row.description,
      'Project Manager': row.projectManager,
      'In Defect': row.inDefect ? 'Yes' : 'No',
      'Original Quote': Number(row.originalQuote),
      'Variations': Number(row.variations),
      'Total Value': Number(row.totalValue),
      //'Invoiced To Date': Number(row.invoicedToDate),
      //'Invoiced This Year': Number(row.invoicedThisYear),
      'Invoiced Total': Number(row.invoicedTotal),
      'Costs To Date': Number(row.costsToDate),
      'Accruals': Number(row.accruals),
      'Retentions Payable': Number(row.retentionsPayable),
      'Committed Costs': Number(row.committedCosts),
      'Total Costs': Number(row.totalCosts),
      'Profit': Number(row.profit),
      'Percentage Profit': Number(row.percentageProfit),
      'Remaining Draw': Number(row.remainingDraw)
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, report.sheetName)
    
    const fileName = `${report.filePrefix}_${new Date().toISOString().split('T')[0]}.xlsx`
    XLSX.writeFile(workbook, fileName)
    
    showToast(`Exported ${exportData.length} records to ${fileName}`, 'success')
  }

  // Export quotes to Excel function
  const exportQuotesToExcel = () => {
    const exportData = quotesDataSorted.map(row => ({
      'Quote Ref': row.refFormatted,
      'Client': row.client || '',
      'Site': row.site || '',
      'Work Type': row.worktype || '',
      'Description': row.description,
      'Quote Value': Number(row.cost),
      'Priority': row.priority || '',
      'Follow-up Date': row.followupDate ? new Date(row.followupDate).toLocaleDateString('en-GB') : '',
      'Created Date': new Date(row.createdDate).toLocaleDateString('en-GB'),
      'Created By': row.employeeCreatedby?.employeeName || '',
      'Assigned To': row.employeeAssignedTo?.employeeName || ''
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Likely Quotes')
    
    const fileName = `likely_quotes_${new Date().toISOString().split('T')[0]}.xlsx`
    XLSX.writeFile(workbook, fileName)
    
    showToast(`Exported ${exportData.length} quotes to ${fileName}`, 'success')
  }

  // Rendered in place of the report body while a fetch is in flight. This used
  // to replace the whole page, which unmounted the filter bar mid-interaction -
  // Kendo's MultiSelect then tried to restore focus to an input that no longer
  // existed and threw "Cannot read properties of null (reading 'focus')".
  const loadingBlock = (
    <div style={{
      textAlign: 'center',
      padding: '4rem 2rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1rem'
    }}>
      <div className="loading-spinner" style={{
        width: '50px',
        height: '50px',
        border: '5px solid #f3f3f3',
        borderTop: '5px solid #1e2a4e',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></div>
      <p style={{ color: '#1e2a4e', fontSize: '1.1rem', fontWeight: 500 }}>
        Loading {report.noun}...
      </p>
    </div>
  )

  return (
    <div>
      <div style={{ padding: "1rem" }}>
        <div className="pipeline__header">
          <h1>{report.title}</h1>
        </div>

        {/* Pipeline filters */}
        <div style={{
          background: '#f5f9fb',
          padding: '1.25rem 1.5rem',
          borderRadius: '8px',
          marginTop: '1rem',
          marginBottom: '1rem',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1.5rem',
          alignItems: 'flex-end'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: '#1e2a4e', fontSize: '13px' }}>
              {report.completedOnly ? 'Completed From' : 'Transaction Start Date'}
            </label>
            <DatePicker
              value={startDate}
              onChange={(e) => setStartDate(e.value)}
              format="dd/MM/yyyy"
              placeholder="No filter"
              style={{ width: '170px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: '#1e2a4e', fontSize: '13px' }}>
              {report.completedOnly ? 'Completed To' : 'Transaction End Date'}
            </label>
            <DatePicker
              value={endDate}
              onChange={(e) => setEndDate(e.value)}
              format="dd/MM/yyyy"
              placeholder="No filter"
              style={{ width: '170px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: '#1e2a4e', fontSize: '13px' }}>
              Department
            </label>
            <MultiSelect
              data={worktypeOptions}
              textField="text"
              dataItemKey="value"
              value={selectedWorktypes}
              onChange={(e: MultiSelectChangeEvent) => setSelectedWorktypes(e.value)}
              filterable={false}
              placeholder="All departments"
              style={{ width: '240px' }}
            />
          </div>
          <ReportUserFilter
            scope={users.scope}
            value={users.selectedIds}
            onChange={users.setSelectedIds}
            label="Project Manager"
            labelStyle={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: '#1e2a4e', fontSize: '13px' }}
            width="240px"
          />
          {/* Completed Jobs only ever shows completed jobs, so it has no switch. */}
          {!report.completedOnly && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <Switch
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.value)}
            />
            <span style={{ fontWeight: 600, color: '#1e2a4e', fontSize: '13px' }}>Include Completed Jobs</span>
          </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              onClick={fetchData}
              themeColor="primary"
              disabled={loading}
            >
              <FaSearch style={{ marginRight: '0.4rem' }} />
              {loading ? 'Loading...' : 'Run Report'}
            </Button>
            <Button
              onClick={() => {
                setStartDate(null)
                setEndDate(null)
                setShowCompleted(false)
                setSelectedWorktypes([])
                users.reset()
              }}
              themeColor="base"
              disabled={loading}
            >
              Clear
            </Button>
          </div>
        </div>

        {loading ? loadingBlock : (
        <>
        {/* Overview: headline figures, then the three questions the pipeline
            gets asked - where the value sits, what is left to bill, and where
            the money is being made or lost. */}
        {pipelineDataSorted.length > 0 && (
          <>
            <div className="pipeline__kpis">
              <div className="pipeline__kpi pipeline__kpi--navy">
                <div className="pipeline__kpi-label">{report.jobsLabel}</div>
                <div className="pipeline__kpi-value">{pipelineDataSorted.length}</div>
              </div>
              {report.showValueKpi && (
                <div className="pipeline__kpi pipeline__kpi--navy">
                  <div className="pipeline__kpi-label">{report.valueLabel}</div>
                  <div className="pipeline__kpi-value">{formatCurrency(totalValue)}</div>
                </div>
              )}
              <div className="pipeline__kpi pipeline__kpi--green">
                <div className="pipeline__kpi-label">{report.invoicedLabel}</div>
                <div className="pipeline__kpi-value">{formatCurrency(totalInvoiced)}</div>
                <div className="pipeline__kpi-sub">{invoicedPct.toFixed(1)}% of value</div>
              </div>
              {report.showRemainingKpi && (
                <div className="pipeline__kpi pipeline__kpi--orange">
                  <div className="pipeline__kpi-label">Remaining to Draw</div>
                  <div className="pipeline__kpi-value">{formatCurrency(totalRemaining)}</div>
                </div>
              )}
              <div className="pipeline__kpi pipeline__kpi--navy">
                <div className="pipeline__kpi-label">Total Costs</div>
                <div className="pipeline__kpi-value">{formatCurrency(totalCosts)}</div>
              </div>
              <div
                className={`pipeline__kpi ${
                  totalProfit >= 0 ? 'pipeline__kpi--green' : 'pipeline__kpi--loss'
                }`}
              >
                <div className="pipeline__kpi-label">{report.profitLabel}</div>
                <div className="pipeline__kpi-value">{formatCurrency(totalProfit)}</div>
                <div className="pipeline__kpi-sub">{marginPct.toFixed(1)}% margin</div>
              </div>
            </div>

            <div className="pipeline__charts">
              {report.showDrawCharts && (
                <div className="pipeline__chart">
                  <h3>Invoiced vs Remaining by Department</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={departmentSummary}>
                      <CartesianGrid stroke="#e9ecef" vertical={false} />
                      <XAxis
                        dataKey="department"
                        tick={axisTick}
                        tickFormatter={truncateCategory}
                        interval={0}
                        angle={-30}
                        textAnchor="end"
                        height={90}
                      />
                      <YAxis tick={axisTick} tickFormatter={formatCompactCurrency} width={70} />
                      <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                      <Legend verticalAlign="top" formatter={legendText} />
                      {/* Stacked, because the two segments add up to the job's
                          total value - the stack height is the department's book.
                          The white stroke is the 2px gap between segments. */}
                      <Bar
                        dataKey="invoicedTotal"
                        name="Invoiced"
                        stackId="value"
                        fill={COLOUR_NAVY}
                        stroke="#ffffff"
                        strokeWidth={2}
                      />
                      <Bar
                        dataKey="remainingDraw"
                        name="Remaining to Draw"
                        stackId="value"
                        fill={COLOUR_GREEN}
                        stroke="#ffffff"
                        strokeWidth={2}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {report.showDrawCharts && (
                <div className="pipeline__chart">
                  <h3>Top 10 Jobs by Remaining to Draw</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    {/* One series, so the title names it and no legend is needed. */}
                    <BarChart
                      data={topRemainingDraw}
                      layout="vertical"
                      margin={{ top: 5, right: 70, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid stroke="#e9ecef" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={axisTick}
                        tickFormatter={formatCompactCurrency}
                      />
                      <YAxis
                        type="category"
                        dataKey="reference"
                        tick={axisTick}
                        width={90}
                      />
                      <Tooltip
                        formatter={(value: any) => formatCurrency(Number(value))}
                        labelFormatter={(label: any) => {
                          const row = topRemainingDraw.find((r) => r.reference === label)
                          return row ? `${row.reference} - ${row.clientName}` : label
                        }}
                      />
                      <Bar
                        dataKey="remainingDraw"
                        name="Remaining to Draw"
                        fill={COLOUR_NAVY}
                        radius={[0, 4, 4, 0]}
                      >
                        <LabelList
                          dataKey="remainingDraw"
                          position="right"
                          formatter={(value: any) => formatCompactCurrency(Number(value))}
                          style={{ fontSize: '9pt', fill: '#5a6472' }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="pipeline__chart">
                <h3>{report.profitLabel} by Department</h3>
                <ResponsiveContainer width="100%" height={300}>
                  {/* Signed values, so colour carries the sign against a zero
                      line. No corner radius: a negative bar would round the
                      wrong end. */}
                  <BarChart data={profitByDepartment}>
                    <CartesianGrid stroke="#e9ecef" vertical={false} />
                    <XAxis
                      dataKey="department"
                      tick={axisTick}
                      tickFormatter={truncateCategory}
                      interval={0}
                      angle={-30}
                      textAnchor="end"
                      height={90}
                    />
                    <YAxis tick={axisTick} tickFormatter={formatCompactCurrency} width={70} />
                    <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                    <ReferenceLine y={0} stroke="#8a94a6" />
                    <Bar dataKey="profit" name={report.profitLabel}>
                      {profitByDepartment.map((row) => (
                        <Cell
                          key={row.department}
                          fill={row.profit >= 0 ? COLOUR_PROFIT : COLOUR_LOSS}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        <TabStrip
        selected={activeTab}
        onSelect={(e) => setActiveTab(e.selected)}
        style={{ marginTop: '1rem' }}
      >
        <TabStripTab title={report.tabTitle}>
          <div style={{ padding: '1rem 0' }}>
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", marginBottom: "1rem" }}>
              <Button
                onClick={exportToExcel}
                icon="excel"
                themeColor="primary"
                data-excel="true"
              >
                <FaFileExcel style={{ marginRight: "0.5rem" }} />
                {report.exportLabel}
              </Button>
            </div>
            
            <div className="pipeline-grid">
              <Grid
                data={pipelineDataSorted.slice(pipelineSkip, pipelineSkip + pipelinePageSize)}
                sortable
                sort={pipelineSort}
                onSortChange={(e) => setPipelineSort(e.sort)}
                filterable
                filter={pipelineFilter}
                onFilterChange={(e) => {
                  setPipelineFilter(e.filter)
                  setPipelineSkip(0) // Reset to first page when filtering
                }}
                pageable
                pageSize={pipelinePageSize}
                skip={pipelineSkip}
                onPageChange={(e) => setPipelineSkip(e.page.skip)}
                total={pipelineDataSorted.length}
                rowRender={pipelineRowRender}
                style={{ height: '600px', width: '100%' }}
              >
              <GridColumn field="reference" title="Job No" width="120px" />
              <GridColumn field="clientName" title="Client" width="200px" />
              <GridColumn field="department" title="Department" width="150px" />
              <GridColumn field="description" title="Description" width="250px" />
              <GridColumn field="projectManager" title="Project Manager" width="150px" />
              <GridColumn 
                field="originalQuote" 
                title="Original Quote" 
                width="130px"
                cell={(props) => (
                  <td>£{Number(props.dataItem.originalQuote).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                )}
              />
              <GridColumn 
                field="variations" 
                title="Variations" 
                width="120px"
                cell={(props) => (
                  <td>£{Number(props.dataItem.variations).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                )}
              />
              <GridColumn 
                field="totalValue" 
                title="Total Value" 
                width="130px"
                cell={(props) => (
                  <td style={{ fontWeight: 'bold' }}>£{Number(props.dataItem.totalValue).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                )}
              />
              <GridColumn 
                field="invoicedTotal" 
                title="Invoiced Total" 
                width="140px"
                cell={(props) => (
                  <td>£{Number(props.dataItem.invoicedTotal).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                )}
              />
              <GridColumn 
                field="costsToDate" 
                title="Costs To Date" 
                width="130px"
                cell={(props) => (
                  <td>£{Number(props.dataItem.costsToDate).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                )}
              />
              <GridColumn 
                field="accruals" 
                title="Accruals" 
                width="120px"
                cell={(props) => (
                  <td>£{Number(props.dataItem.accruals).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                )}
              />
              <GridColumn
                field="retentionsPayable"
                title="Retentions Payable"
                width="150px"
                cell={(props) => (
                  <td>£{Number(props.dataItem.retentionsPayable).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                )}
              />
              <GridColumn
                field="committedCosts"
                title="Committed Costs"
                width="140px"
                cell={(props) => (
                  <td>£{Number(props.dataItem.committedCosts ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                )}
              />
              <GridColumn
                field="totalCosts"
                title="Total Costs"
                width="120px"
                cell={(props) => (
                  <td>£{Number(props.dataItem.totalCosts).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                )}
              />
              <GridColumn 
                field="profit" 
                title="Profit" 
                width="120px"
                cell={(props) => {
                  const profit = Number(props.dataItem.profit);
                  const color = profit >= 0 ? '#217346' : '#d32f2f';
                  return (
                    <td style={{ color, fontWeight: 600 }}>
                      £{profit.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  );
                }}
              />
              <GridColumn 
                field="percentageProfit" 
                title="Percentage Profit" 
                width="130px"
                cell={(props) => {
                  const percentageProfit = Number(props.dataItem.percentageProfit);
                  const color = percentageProfit >= 0 ? '#217346' : '#d32f2f';
                  return (
                    <td style={{ color, fontWeight: 600 }}>
                      {percentageProfit.toFixed(2)}%
                    </td>
                  );
                }}
              />
              <GridColumn 
                field="remainingDraw" 
                title="Remaining Draw" 
                width="140px"
                cell={(props) => (
                  <td>£{Number(props.dataItem.remainingDraw).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                )}
              />
            </Grid>
            </div>
          </div>
        </TabStripTab>

        <TabStripTab title="Likely Quotes">
          <div style={{ padding: '1rem 0' }}>
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", marginBottom: "1rem" }}>
              <Button
                onClick={exportQuotesToExcel}
                icon="excel"
                themeColor="primary"
                data-excel="true"
              >
                <FaFileExcel style={{ marginRight: "0.5rem" }} />
                Export Likely Quotes
              </Button>
            </div>
            
            {quotesLoading ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                Loading quotes...
              </div>
            ) : (
              <div className="likely-quotes-grid">
                <Grid
                  data={quotesDataSorted.slice(quotesSkip, quotesSkip + quotesPageSize)}
                  sortable
                  sort={quotesSort}
                  onSortChange={(e) => setQuotesSort(e.sort)}
                  filterable
                  filter={quotesFilter}
                  onFilterChange={(e) => {
                    setQuotesFilter(e.filter)
                    setQuotesSkip(0) // Reset to first page when filtering
                  }}
                  pageable
                  pageSize={quotesPageSize}
                  skip={quotesSkip}
                  onPageChange={(e) => setQuotesSkip(e.page.skip)}
                  total={quotesDataFiltered.length}
                  style={{ height: '600px', width: '100%' }}
                >
                <GridColumn 
                  field="refFormatted" 
                  title="Quote Ref" 
                  width="120px"
                  cell={(props) => (
                    <td style={{ fontWeight: 'bold' }}>{props.dataItem.refFormatted}</td>
                  )}
                />
                <GridColumn 
                  field="client.clientName" 
                  title="Client" 
                  width="150px"
                  cell={(props) => (
                    <td>{props.dataItem.client || ''}</td>
                  )}
                />
                <GridColumn 
                  field="site.siteAddr" 
                  title="Site" 
                  width="150px"
                  cell={(props) => (
                    <td>{props.dataItem.site || ''}</td>
                  )}
                />
                <GridColumn 
                  field="worktype.description" 
                  title="Work Type" 
                  width="120px"
                  cell={(props) => (
                    <td>{props.dataItem.worktype || ''}</td>
                  )}
                />
                <GridColumn field="clientName" title="Client" width="200px" />
                <GridColumn field="description" title="Description" width="200px" />
                <GridColumn 
                  field="cost" 
                  title="Quote Value" 
                  width="130px"
                  cell={(props) => (
                    <td style={{ fontWeight: 'bold' }}>£{Number(props.dataItem.cost).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  )}
                />
                <GridColumn 
                  field="priority.description" 
                  title="Priority" 
                  width="100px"
                  cell={(props) => (
                    <td>{props.dataItem.priority || ''}</td>
                  )}
                />
                <GridColumn 
                  field="followupDate" 
                  title="Follow-up Date" 
                  width="130px"
                  cell={(props) => (
                    <td>{props.dataItem.followupDate ? new Date(props.dataItem.followupDate).toLocaleDateString('en-GB') : ''}</td>
                  )}
                />
                <GridColumn 
                  field="createdDate" 
                  title="Created Date" 
                  width="120px"
                  cell={(props) => (
                    <td>{new Date(props.dataItem.createdDate).toLocaleDateString('en-GB')}</td>
                  )}
                />
                <GridColumn 
                  field="employeeCreatedby.employeeName" 
                  title="Created By" 
                  width="120px"
                  cell={(props) => (
                    <td>{props.dataItem.createdBy || ''}</td>
                  )}
                />
                <GridColumn 
                  field="employeeAssignedTo.employeeName" 
                  title="Assigned To" 
                  width="120px"
                  cell={(props) => (
                    <td>{props.dataItem.assignedTo || ''}</td>
                  )}
                />
              </Grid>
              </div>
            )}
          </div>
        </TabStripTab>
      </TabStrip>
        </>
        )}
      </div>
    </div>
  )
}

export default Pipeline